import { Box, CircularProgress } from '@mui/material';
import { useEffect, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useSession } from './hooks/useSession';
import { Board } from './pages/Board';
import { Admin } from './pages/Admin';
import { Install } from './pages/Install';
import { Join } from './pages/Join';
import { MarketDetail } from './pages/MarketDetail';
import { Me } from './pages/Me';
import { NewMarket } from './pages/NewMarket';
import { Settle } from './pages/Settle';
import { SignIn } from './pages/SignIn';
import { Standings } from './pages/Standings';
import { AUTH_EXPIRED_EVENT } from './services/api-client';
import { useAuthStore } from './stores/authStore';

export function App() {
    const session = useAuthStore((s) => s.session);
    const clearSession = useAuthStore((s) => s.clearSession);
    const probe = useSession();

    // The api client raises this once a refresh has already failed. Handling it
    // here rather than in every hook keeps the sign-out path single.
    //
    // This must not clear the query cache. Doing so evicts the session probe
    // while its observer is still mounted, React Query immediately refetches
    // it, that refetch 401s and raises this event again -- a loop that renders
    // as a permanent spinner instead of a redirect to sign-in. Dropping the
    // session is enough; the failed queries settle into their error state on
    // their own, and an explicit sign-out still clears the cache because it
    // navigates away from every observer.
    useEffect(() => {
        const onExpired = (): void => {
            clearSession();
        };
        window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
        return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
    }, [clearSession]);

    return (
        <Routes>
            {/* Public: the shared link that gets texted to the group. */}
            <Route path="/join/:code" element={<Join />} />
            <Route path="/signin" element={<SignIn />} />

            <Route
                path="*"
                element={
                    <RequireSession isProbing={probe.isPending} hasSession={session !== null}>
                        <AppShell>
                            <ErrorBoundary>
                                <Routes>
                                    <Route path="/" element={<Board />} />
                                    <Route path="/new" element={<NewMarket />} />
                                    <Route path="/markets/:marketId" element={<MarketDetail />} />
                                    <Route
                                        path="/markets/:marketId/settle"
                                        element={<Settle />}
                                    />
                                    <Route path="/admin" element={<Admin />} />
                                    <Route path="/standings" element={<Standings />} />
                                    <Route path="/me" element={<Me />} />
                                    <Route path="/install" element={<Install />} />
                                    <Route path="*" element={<Navigate to="/" replace />} />
                                </Routes>
                            </ErrorBoundary>
                        </AppShell>
                    </RequireSession>
                }
            />
        </Routes>
    );
}

interface RequireSessionProps {
    isProbing: boolean;
    hasSession: boolean;
    children: ReactNode;
}

function RequireSession({ isProbing, hasSession, children }: RequireSessionProps) {
    const location = useLocation();

    // Waiting for the first /me probe, so a signed-in reload does not flash the
    // sign-in screen before the session resolves.
    if (isProbing && !hasSession) {
        return (
            <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!hasSession) {
        // Carries the attempted location so signing in returns there.
        return <Navigate to="/signin" replace state={{ from: location.pathname }} />;
    }

    return children;
}
